import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Notice, Spinner } from "@/components/common/States";
import { ACCESS_STATE, formatDate, ROLE_LABELS } from "@/lib/labels";
import { api, errorMessage } from "@/lib/api";

// Tindakan founder atas tagihan. Tanpa unggah bukti: yang dicatat hanyalah penilaian founder,
// dan alasannya wajib agar jejak audit tidak bergantung pada pengunggah berkas.
export const ACTIONS = {
  CLAIMED: { label: "Tandai sudah bayar ke reseller", ringkas: "sudah bayar", bahaya: false,
     penjelasan: "Lapis pertama: sekolah sudah menyetor ke reseller. Akun belum lunas ke founder, jadi setelah tanggal 3 ia berstatus “Menunggu verifikasi pembayaran” dan bisa terkunci pada tanggal 8 sampai founder menekan Aktifkan." },
  ACTIVE: { label: "Aktifkan akun", ringkas: "aktifkan", bahaya: false,
     penjelasan: "Founder menyatakan pembayaran akun sudah diterima dan mengaktifkannya. Akun berstatus lunas untuk periode ini dan tidak bisa terkunci oleh tanggal." },
  WAIVED: { label: "Gratiskan", ringkas: "gratiskan", bahaya: false,
     penjelasan: "Tagihan dihapus tanpa pembayaran dan akses langsung aktif. Tidak tercatat sebagai piutang reseller, dan alasannya wajib diisi agar bisa ditelusuri." },
  PENDING: { label: "Batal penilaian", ringkas: "batal", bahaya: true,
     penjelasan: "Kembalikan ke belum bayar: tagihan jatuh ke tenggang biasa dan bisa terkunci mulai tanggal 4. Jejak penilaian sebelumnya dihapus dari baris tagihan." },
};

export function ActionDialog({ open, onOpenChange, action, jumlah, scope, targetLabel, periode, bukti, rincian, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dipilih, setDipilih] = useState([]);

  // Hanya akun yang dicentang yang diaktifkan; default semua terpilih agar perilaku
  // tombol "Aktifkan semua akun" di antrean tidak berubah.
  useEffect(() => {
    setDipilih(open && rincian?.length ? rincian.map((a) => a.bill_id) : []);
  }, [open, action, rincian]);

  if (!action) return null;
  const meta = ACTIONS[action];
  const totalRincian = rincian?.length || 0;
  const semuaDipilih = totalRincian > 0 && dipilih.length === totalRincian;
  const toggleAkun = (id) => setDipilih((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleSemua = () => setDipilih(semuaDipilih ? [] : rincian.map((a) => a.bill_id));

  const tutup = (o) => {
    if (!o) {
      setReason("");
      setErr("");
    }
    onOpenChange(o);
  };

  const submit = async () => {
    if (reason.trim().length < 5) return setErr("Alasan wajib diisi, minimal 5 karakter, dan tercatat di audit.");
    if (totalRincian > 0 && !dipilih.length) return setErr("Pilih minimal satu akun yang akan diaktifkan.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/platform/billing/bills/status", {
        periode, status: action, reason: reason.trim(), ...scope,
        ...(totalRincian > 0 ? { bill_ids: dipilih } : {}),
      });
      const kondisi = ACCESS_STATE[data.akses_setelah]?.label;
      toast.success(`${meta.ringkas}: ${data.diubah} akun diubah${data.sudah_benar ? `, ${data.sudah_benar} sudah berstatus begitu` : ""}${kondisi ? ` · kondisi akses: ${kondisi}` : ""}.`);
      setReason("");
      onOpenChange(false);
      onDone(data);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={tutup}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="billing-action-dialog">
        <DialogHeader>
          <DialogTitle>
            {meta.label}{targetLabel ? ` — ${targetLabel}` : ""}
            {totalRincian > 0 ? ` · ${dipilih.length} dari ${totalRincian} akun` : jumlah ? ` · ${jumlah} akun` : targetLabel ? " · seluruh akun" : ""}
          </DialogTitle>
          <DialogDescription>{meta.penjelasan}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rincian?.length > 0 && (
            <>
            <div className="rounded-xl border" data-testid="billing-action-accounts">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                <p className="text-sm font-semibold">
                  {rincian.length} akun sudah dibayar dan mengajukan aktivasi
                </p>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium" htmlFor="billing-action-pilih-semua">
                  <input id="billing-action-pilih-semua" type="checkbox" className="h-4 w-4 accent-[#3B82F6]"
                    checked={semuaDipilih} onChange={toggleSemua} data-testid="billing-action-pilih-semua" />
                  Pilih semua
                </label>
              </div>
              <ul className="max-h-56 divide-y overflow-y-auto text-sm">
                {rincian.map((a) => (
                  <li key={a.bill_id} className="px-3 py-2" data-testid={`billing-action-account-${a.bill_id}`}>
                    <label className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5"
                      htmlFor={`billing-action-pilih-${a.bill_id}`}>
                      <input id={`billing-action-pilih-${a.bill_id}`} type="checkbox"
                        className="h-4 w-4 shrink-0 accent-[#3B82F6]" checked={dipilih.includes(a.bill_id)}
                        onChange={() => toggleAkun(a.bill_id)} data-testid={`billing-action-pilih-${a.bill_id}`} />
                      <span className="font-medium">{a.full_name}</span>
                      <span className="text-xs text-muted-foreground">{ROLE_LABELS[a.role] || a.role || "Akun sekolah"}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {a.diminta_pada ? `diajukan ${formatDate(a.diminta_pada)}`
                          : a.dilaporkan_pada ? `dilaporkan ${formatDate(a.dilaporkan_pada)}` : ""}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {dipilih.length > 0 && dipilih.length < rincian.length && (
              <Notice tone="warn" testId="billing-action-sebagian">
                Hanya {dipilih.length} akun yang akan diaktifkan. {rincian.length - dipilih.length} akun lain
                tetap menunggu aktivasi.
              </Notice>
            )}
            </>
          )}
          <Notice tone="muted" testId="billing-action-no-proof">
            <p className="font-semibold">Tidak perlu mengunggah bukti pembayaran.</p>
            <p className="mt-1">Yang dicatat adalah penilaian founder; sistem memeriksa sendiri jumlah akun dan setorannya.
              Kalau angka setoran dan jumlah akun tidak cocok, founder mengecek langsung ke reseller sebelum mengubah status.</p>
          </Notice>
          {!jumlah && <Notice tone="warn" testId="billing-action-collective">
            Tindakan ini mengenai semua tagihan {targetLabel} pada periode {periode}, termasuk baris yang tidak tampil di layar.
          </Notice>}
          {bukti && <p className="text-sm text-muted-foreground">{bukti}</p>}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="billing-action-reason">Alasan (wajib, tercatat di audit)</label>
            <Textarea id="billing-action-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: bendahara sekolah setor kolektif 12 November" data-testid="billing-action-reason-input" />
          </div>
          {err && <Notice tone="danger" testId="billing-action-error">{err}</Notice>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => tutup(false)} data-testid="billing-action-cancel">Batal</Button>
          <Button variant={meta.bahaya ? "destructive" : "default"} onClick={submit}
            disabled={busy || (totalRincian > 0 && !dipilih.length)} data-testid="billing-action-submit">
            {busy && <Spinner />} {meta.label}{totalRincian > 0 ? ` · ${dipilih.length} akun` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
